// machine-settings.js — per-machine daemon settings editor (Machines page).
//
// Lets a machine's OWNER edit that machine's daemon settings from the hosted
// dashboard. The settings sync to the machine and its daemon applies them as a
// layer ABOVE local config but BELOW any team-locked company standard (migration
// 052). Owner-only is enforced server-side; this UI only ever lists the user's
// own machines.
//
// Wire shape: GET/PUT /v1/me/machines/settings?laptop_id=<id>
//   GET  → { laptop_id, version, set_keys: [<key>], values: {<key>: <typed>} }
//   PUT  → { values: {<key>: <typed>} }   (partial; only changed keys)
//
// Renders the form inline on the dedicated Settings page (renderMachineSettingsInline);
// uses api() for the cloud→coordinator call.
import { api, apiAsUser } from "./api.js";
import { escapeHTML as esc } from "./util.js";
import { SETTING_SECTIONS, SETTING_SPECS } from "./settings-schema.gen.js";
import { agentLogo } from "./agent-logos.js";

const TOOL_LABEL = {
  "claude-code": "Claude Code", cursor: "Cursor", "claude-desktop": "Claude Desktop", "copilot-cli": "Copilot CLI",
  amp: "Amp", codex: "Codex CLI", gemini: "Gemini CLI", windsurf: "Windsurf", cline: "Cline", continue: "Continue", aider: "Aider",
};

// toolAction is the button a tool row offers, if any: Repair when the
// machine's entry for it is broken, Connect when the tool is installed but not
// connected (for a per-repo tool: in every repo the machine knows).
function toolAction(t) {
  if (t.state === "stale") return { action: "repair", label: "Repair" };
  if (t.repo) {
    if (!t.repos_total || t.repos_on >= t.repos_total || !t.found) return null;
    return { action: "connect", label: t.repos_total === 1 ? "Connect" : `Connect in all ${t.repos_total} repos` };
  }
  if (t.state === "off" && t.found) return { action: "connect", label: "Connect" };
  return null;
}

// How AgentCollision protects each tool once connected. Hooks run before
// every edit and can stop it; MCP tools get AgentCollision as a tool their
// AI is asked to use; Aider is checked at commit time.
const TOOL_STRENGTH = {
  "claude-code": "hooks", codex: "hooks", cursor: "hooks", gemini: "hooks", windsurf: "hooks", cline: "hooks",
  "claude-desktop": "mcp", "copilot-cli": "mcp", continue: "mcp", amp: "mcp", aider: "advisory",
};
const STRENGTH_TEXT = {
  hooks: ["Blocks clashes", "Before this tool's AI edits a file, AgentCollision checks whether another chat is editing it, and stops the edit if so."],
  mcp: ["Asks first", "This tool's AI can see which files other chats are editing and is asked to check before editing. It isn't forced to."],
  advisory: ["At commit", "Checked when you commit: a commit touching a file another chat is editing is stopped."],
};

// toolsHTML is a compact grid of the AI tools a machine reported
// (effective._tools). Tools neither installed nor connected are left out.
function toolsHTML(effective, label) {
  const tools = effective && effective._tools;
  const who = esc(label || "this machine");
  const head = `<div class="ms-tools-head">
      <span>Connected tools</span>
      <span class="help-icon" data-tooltip="Connecting a tool adds AgentCollision to it, so chats in that tool and chats in your other tools don't change the same file at the same time.">?</span>
    </div>
    <p class="ms-tools-sub">Connected tools check with AgentCollision before their AI changes a file, so two chats never overwrite each other's work, even across different tools.</p>`;
  if (!Array.isArray(tools)) {
    return head + `<p class="ms-tools-sub">${who} hasn't reported its tools yet. They appear here within a minute of it running the latest AgentCollision.</p>`;
  }
  const shown = tools.filter((t) => t.found || t.state !== "off");
  if (!shown.length) return head + `<p class="ms-tools-sub">No AI coding tools found on ${who}.</p>`;
  const order = { stale: 0, off: 1, connected: 2 };
  shown.sort((a, b) => (order[a.state] ?? 1) - (order[b.state] ?? 1) || (TOOL_LABEL[a.id] || a.id).localeCompare(TOOL_LABEL[b.id] || b.id));
  const status = (t) => {
    if (t.state === "stale") return { cls: "warn", text: "Needs repair" };
    if (t.state === "connected") {
      const partial = t.repo && t.repos_on < t.repos_total;
      return { cls: partial ? "part" : "on", text: t.repo ? `${t.repos_on} of ${t.repos_total} repos` : "Connected" };
    }
    return { cls: "off", text: "Not connected" };
  };
  const tiles = shown.map((t) => {
    const st = status(t);
    const [strength, strengthTip] = STRENGTH_TEXT[TOOL_STRENGTH[t.id]] || ["", ""];
    const a = toolAction(t);
    const btn = a ? `<button type="button" class="btn btn-sm ms-tool-btn" data-tool-action="${a.action}" data-tool-id="${esc(t.id)}">${esc(a.action === "repair" ? "Repair" : "Connect")}</button>` : "";
    return `<div class="ms-tool ms-tool-${st.cls}">
        <span class="ms-tool-logo">${agentLogo(t.id, 24) || ""}</span>
        <span class="ms-tool-text">
          <span class="ms-tool-name">${esc(TOOL_LABEL[t.id] || t.id)}</span>
          <span class="ms-tool-state"><span class="ms-dot"></span><span>${esc(st.text)}</span>${strength ? `<span data-tooltip="${esc(strengthTip)}">· ${esc(strength)}</span>` : ""}</span>
        </span>
        ${btn}
      </div>`;
  }).join("");
  const msgs = shown.map((t) => `<div class="ms-tool-status" data-tool-status="${esc(t.id)}" role="status" aria-live="polite"></div>`).join("");
  return head + `<div class="ms-tools-grid">${tiles}</div><div class="ms-tools-msgs">${msgs}</div>
    <p class="ms-tools-foot">Connect and Repair ask ${who} to set it up, so it needs to be on and running AgentCollision. Per-repo tools are set up in every repo it knows.</p>`;
}

const TOOL_ACTION_WAIT_NOTE_MS = 20000;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// runToolAction asks the machine to connect or repair one tool, then follows
// the request until the machine answers, gives up, or the request expires.
async function runToolAction(container, laptopID, label, online, btn, onDone) {
  const id = btn.dataset.toolId;
  const who = label || "your computer";
  // Look the line up each time: a refresh redraws the tool rows.
  const say = (text, color) => {
    const statusEl = container.querySelector(`[data-tool-status="${CSS.escape(id)}"]`);
    if (!statusEl) return;
    // Shown under the grid, so name the tool unless the message already does.
    const name = TOOL_LABEL[id] || id;
    statusEl.textContent = text && !text.includes(name) ? name + ": " + text : text;
    statusEl.style.color = color || "var(--text-tertiary)";
  };
  const path = "/v1/me/machines/tool-actions?laptop_id=" + encodeURIComponent(laptopID);
  btn.disabled = true;
  let queued;
  try {
    queued = await apiAsUser(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tool_id: id, action: btn.dataset.toolAction }),
    });
  } catch (err) {
    say("Not sent: " + ((err && err.message) || "unknown error"), "var(--danger)");
    btn.disabled = false;
    return;
  }
  say(online === false
    ? `Asked ${who}… It looks offline, so this happens when it's back on (within 5 minutes).`
    : `Asked ${who}…`);
  const started = Date.now();
  // Stop following if the page moved on to another machine.
  const stillShown = () => container.isConnected && container.dataset.msLaptop === laptopID;
  const enable = () => { const b = container.querySelector(`[data-tool-id="${CSS.escape(id)}"]`); if (b) b.disabled = false; };
  while (stillShown()) {
    await sleep(2000);
    let list;
    try { list = await apiAsUser(path); } catch { continue; }
    const a = ((list && list.actions) || []).find((x) => x.id === queued.action.id);
    if (!a) continue;
    if (a.status === "done") { say(a.message || "Done.", "var(--success)"); onDone(); return; }
    if (a.status === "failed") { say(a.message || "It didn't work.", "var(--danger)"); enable(); return; }
    if (a.status === "expired") {
      say(`${who} didn't answer within 5 minutes. It's probably off, asleep or not running AgentCollision. Try again once it's on.`, "var(--warning)");
      enable();
      return;
    }
    if (a.status === "pending" && Date.now() - started > TOOL_ACTION_WAIT_NOTE_MS) {
      say(`Still waiting for ${who}. It may be off or asleep; the request is dropped after 5 minutes.`);
    }
  }
}

// The hosted page cannot ask a machine's daemon for its schema, so it renders
// from the daemon's spec table compiled into settings-schema.gen.js. Only the
// keys the coordinator stores per machine are shown (MACHINE_SETTINGS_SPEC in
// infra/workers/coordinator.ts); telemetry is left out because the daemon
// reads it from a file on the machine, not from config.
const HOSTED_KEYS = new Set([
  "cooldown_secs", "lease_ttl_secs", "orphan_threshold_secs", "track_reads", "allow_revoke", "log_level",
  "watch_unmanaged_writes_enabled", "auto_install_pre_commit_hook",
  "git_context_enabled", "git_context_detail", "git_context_lookback_hours", "git_context_max_commits",
  "git_context_churn_threshold", "git_context_include_prs", "git_context_include_worktrees", "git_context_timeout_secs",
  "worktree_alloc_enabled", "worktree_alloc_base_dir", "worktree_alloc_branch_prefix", "worktree_alloc_repos",
  "worktree_gc_enabled", "worktree_preserve_dirty", "worktree_max_disk_gb",
]);
// The coordinator's length limits for text settings.
const TEXT_MAX = { worktree_alloc_base_dir: 256, worktree_alloc_branch_prefix: 64, worktree_alloc_repos: 512 };
const SECTION_TITLE = Object.fromEntries(SETTING_SECTIONS.map((s) => [s.id, s.title]));

const MACHINE_SETTINGS_SPEC = SETTING_SPECS.filter((s) => HOSTED_KEYS.has(s.key)).map((s) => ({
  key: s.key,
  section: SECTION_TITLE[s.section] || s.section,
  label: s.label,
  help: s.desc,
  tip: s.tip,
  type: s.type === "text" ? "str" : s.type,
  options: s.options,
  def: s.default,
  min: s.min,
  max: s.type === "text" ? TEXT_MAX[s.key] : s.max,
  unit: s.unit,
  zeroMeans: s.zero_means,
}));

// renderControl builds the HTML control for one spec row, pre-filled with the
// current value (or the empty/unset state). Number/text inputs show the shipped
// DEFAULT as a placeholder (so a blank field reads as "= default", not an empty
// void) and a unit suffix beside them. Uses the dashboard's own control classes
// (.toggle-switch / .setting-number / .setting-text / .setting-select).
// placeholderFor returns the value an UNSET field's input shows greyed-in: the
// daemon-reported EFFECTIVE value (what's actually running on this machine) when
// available, else the shipped default. So a machine whose local config.toml sets
// a value the dashboard hasn't pinned shows that real value, not the ship default.
function placeholderFor(spec, effective) {
  if (effective && effective[spec.key] !== undefined && effective[spec.key] !== null) {
    return String(effective[spec.key]);
  }
  return spec.def !== undefined ? String(spec.def) : undefined;
}

// shownValue is what a bool/enum control displays: this machine's override
// if it has one, else the value actually in effect (daemon-reported), else
// the shipped default. Showing `false` / the first option for "no override"
// made default-ON settings render OFF.
function shownValue(spec, value, effective) {
  if (value !== undefined) return value;
  if (effective && effective[spec.key] !== undefined && effective[spec.key] !== null) return effective[spec.key];
  return spec.def;
}

function renderControl(spec, value, effective) {
  const id = "machine-set-" + spec.key;
  if (spec.type === "bool" || spec.type === "enum") value = shownValue(spec, value, effective);
  if (spec.type === "bool") {
    const checked = value === true ? " checked" : "";
    return `<label class="toggle-switch"><input type="checkbox" id="${id}" data-key="${spec.key}" data-type="bool"${checked} /><span class="toggle-slider"></span></label>`;
  }
  if (spec.type === "enum") {
    const opts = spec.options.map((o) => `<option value="${esc(o.value)}"${o.value === value ? " selected" : ""}>${esc(o.label)}</option>`).join("");
    return `<select id="${id}" data-key="${spec.key}" data-type="enum" class="setting-select">${opts}</select>`;
  }
  const unit = spec.unit ? `<span class="setting-unit">${esc(spec.unit)}</span>` : "";
  const phVal = placeholderFor(spec, effective);
  const ph = phVal !== undefined ? ` placeholder="${esc(phVal)}"` : "";
  if (spec.type === "str") {
    const sv = value === undefined || value === null ? "" : esc(String(value));
    return `<span class="setting-control-wrap"><input type="text" id="${id}" data-key="${spec.key}" data-type="str" class="setting-text" value="${sv}"${ph} maxlength="${spec.max}" />${unit}</span>`;
  }
  // int — placeholder shows the effective (or default) value; a blank field
  // therefore reads as "= what's running now".
  const v = value === undefined || value === null ? "" : esc(String(value));
  return `<span class="setting-control-wrap"><input type="number" id="${id}" data-key="${spec.key}" data-type="int" class="setting-number" value="${v}"${ph}${spec.min !== undefined ? ` min="${spec.min}"` : ""}${spec.max !== undefined ? ` max="${spec.max}"` : ""} />${unit}</span>`;
}

// settingHint renders the per-row description suffix. When the daemon has
// reported an EFFECTIVE value for this (unset) key, show "Currently: X" — what's
// actually running. Otherwise show "Default: X · range a–b" (the shipped value).
function settingHint(spec, effective) {
  const fmt = (raw, isEffective) => {
    let d;
    if (spec.type === "bool") d = raw ? "on" : "off";
    else if (spec.type === "str") d = (raw === "" || raw === undefined) ? "empty" : String(raw);
    else if (spec.type === "enum") d = ((spec.options || []).find((o) => o.value === raw) || { label: String(raw) }).label;
    else if (raw === 0 && spec.zeroMeans) d = spec.zeroMeans;
    else d = String(raw) + (spec.unit ? " " + spec.unit : "");
    let s = (isEffective ? "Currently: " : "Default: ") + d;
    if (!isEffective && spec.type === "int" && spec.min !== undefined && spec.max !== undefined) {
      s += " · range " + spec.min + "–" + spec.max;
    } else if (!isEffective && spec.type === "int" && spec.min !== undefined) {
      s += " · at least " + spec.min;
    }
    return s;
  };
  const hasEff = effective && effective[spec.key] !== undefined && effective[spec.key] !== null;
  if (hasEff) return ` <span style="color: var(--text-tertiary);">${esc(fmt(effective[spec.key], true))}</span>`;
  if (spec.def !== undefined) return ` <span style="color: var(--text-tertiary);">${esc(fmt(spec.def, false))}</span>`;
  return "";
}

// settingRow renders one label+control row using the dashboard's .setting-row /
// .setting-info / .setting-label / .setting-desc classes. The label carries a
// .help-icon "?" whose hover (data-tooltip) explains WHEN you'd change the
// setting; the desc shows the one-liner + the default/range. Team-locked keys
// render read-only with 🔒.
function settingRow(spec, values, locked, effective) {
  const isLocked = locked.has(spec.key);
  const control = isLocked
    ? `<span class="setting-locked-note" style="font-size: 12px; color: var(--text-tertiary);">enforced by team</span>`
    : renderControl(spec, values[spec.key], effective);
  const helpIcon = spec.tip
    ? ` <span class="help-icon" data-tooltip="${esc(spec.tip)}">?</span>`
    : "";
  const hint = settingHint(spec, effective);
  const desc = (spec.help || hint)
    ? `<div class="setting-desc">${spec.help ? esc(spec.help) : ""}${hint}</div>`
    : "";
  return `<div class="setting-row">
    <div class="setting-info">
      <div class="setting-label">${esc(spec.label)}${isLocked ? ' 🔒' : ''}${helpIcon}</div>
      ${desc}
    </div>
    ${control}
  </div>`;
}

// formHTML builds the settings body as HORIZONTAL TABS (one per section) so the
// user picks a section instead of scrolling a long list. A .settings-tabs
// tab bar sits on top; below it, one panel per section with only the active
// panel visible. Tab switching is wired by wireSectionTabs (called after the
// HTML is in the DOM). All sections' controls are rendered up-front (hidden
// panels included) so readForm/diffAndSave see every field regardless of which
// tab is showing.
function formHTML(values, lockedKeys, effective, footer) {
  const locked = new Set(lockedKeys || []);
  // Preserve the section order of first appearance in the spec.
  const order = [];
  const bySection = {};
  for (const spec of MACHINE_SETTINGS_SPEC) {
    const sec = spec.section || "Other";
    if (!bySection[sec]) { bySection[sec] = []; order.push(sec); }
    bySection[sec].push(spec);
  }
  const tabs = order.map((sec, i) =>
    `<button type="button" class="${i === 0 ? "active" : ""}" role="tab" data-settings-tab="${esc(sec)}">${esc(sec)}</button>`
  ).join("");
  const panels = order.map((sec, i) => {
    const rows = bySection[sec].map((spec) => settingRow(spec, values, locked, effective)).join("");
    return `<div class="settings-tab-panel" data-settings-panel="${esc(sec)}"${i === 0 ? "" : ' style="display:none;"'}>${rows}</div>`;
  }).join("");
  return `
    <div style="padding: 8px 20px 0;">
      <div class="settings-tabs" role="tablist" id="settings-tabs">${tabs}</div>
    </div>
    ${panels}
    <div style="padding: 12px 20px 0; font-size: 11px; color: var(--text-tertiary);">${esc(footer || "These apply to this machine's daemon within ~10s. A 🔒 setting is an enforced team standard and can't be overridden here.")}</div>`;
}

// wireSectionTabs activates the horizontal tab bar inside `root`: clicking a tab
// shows its panel and hides the others, and marks the active button. Idempotent
// per render (a fresh root each time renderMachineSettingsInline runs).
function wireSectionTabs(root) {
  if (!root) return;
  const bar = root.querySelector("#settings-tabs");
  if (!bar) return;
  bar.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-settings-tab]");
    if (!btn) return;
    const sec = btn.getAttribute("data-settings-tab");
    bar.querySelectorAll("[data-settings-tab]").forEach((b) => b.classList.toggle("active", b === btn));
    root.querySelectorAll("[data-settings-panel]").forEach((p) => {
      p.style.display = p.getAttribute("data-settings-panel") === sec ? "" : "none";
    });
  });
}

// unsetDefault is the value a control SHOWS when this machine has no override for
// the key (the GET omits unset keys, so the form falls back to these). Used to
// decide "did the user actually change anything?" — a bool or enum with no
// override shows the value in effect (shownValue), so a Save with no
// interaction must NOT pin it. Mirrors renderControl.
function unsetDefault(spec, effective) {
  // Must equal what renderControl shows for "no override", or an untouched
  // Save would pin the displayed value as an override.
  if (spec.type === "bool" || spec.type === "enum") return shownValue(spec, undefined, effective);
  if (spec.type === "str") return ""; // empty text → omitted by readForm
  return undefined; // int: empty input → omitted by readForm
}

// readForm collects the values currently in the form, coercing per type. Only
// keys whose control exists (not team-locked) are read.
function readForm() {
  const out = {};
  for (const spec of MACHINE_SETTINGS_SPEC) {
    const el = document.getElementById("machine-set-" + spec.key);
    if (!el) continue; // locked → no control
    if (spec.type === "bool") {
      out[spec.key] = !!el.checked;
    } else if (spec.type === "enum") {
      out[spec.key] = el.value;
    } else if (spec.type === "str") {
      // Omit an empty string, mirroring how a blank int is omitted below — so a
      // blank str field is treated as "no value here" rather than pinning "" as
      // an override. Keeps str and int consistent: neither pins on blank. (The
      // daemon falls back to its own default for an unset key, e.g. empty
      // base_dir = ~/ac-worktrees.) Clearing an existing override isn't an
      // affordance here in either type; this just avoids accidentally pinning "".
      const sv = el.value.trim();
      if (sv !== "") out[spec.key] = sv;
    } else {
      const n = parseInt(el.value, 10);
      if (Number.isFinite(n)) out[spec.key] = n;
    }
  }
  return out;
}

// loadMachineSettings fetches a machine's current override + the team's locked
// keys + the daemon-reported EFFECTIVE settings. Used by the inline Settings
// page. Returns { values, lockedKeys, effective }. `effective` is absent when
// the machine's daemon hasn't reported (old daemon / just connected) → the UI
// falls back to the shipped default.
async function loadMachineSettings(laptopID) {
  const current = await api("/v1/me/machines/settings?laptop_id=" + encodeURIComponent(laptopID));
  const values = (current && current.values) || {};
  const effective = (current && current.effective) || {};
  let lockedKeys = [];
  try {
    const team = await api("/v1/teams/settings");
    lockedKeys = (team && team.locked_keys) || [];
  } catch {
    /* best-effort — if team settings don't load, nothing renders locked */
  }
  return { values, lockedKeys, effective };
}

// diffAndSave PUTs every value that differs from what the form was rendered
// from (so an untouched control is never pinned) and returns the saved keys.
async function diffAndSave(laptopID, loadedValues, effective) {
  const next = readForm();
  const specByKey = Object.fromEntries(MACHINE_SETTINGS_SPEC.map((s) => [s.key, s]));
  const changed = {};
  for (const [k, v] of Object.entries(next)) {
    const loaded = loadedValues[k] !== undefined ? loadedValues[k] : unsetDefault(specByKey[k], effective);
    if (JSON.stringify(v) !== JSON.stringify(loaded)) changed[k] = v;
  }
  if (Object.keys(changed).length === 0) return [];
  await apiAsUser("/v1/me/machines/settings?laptop_id=" + encodeURIComponent(laptopID), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ values: changed }),
  });
  Object.assign(loadedValues, changed);
  return Object.keys(changed);
}

// renderMachineSettingsInline renders one machine's settings into the
// Settings page. Each change saves on its own, like the local Settings page:
// a status line under the form says Saving / Saved / the error, and that the
// machine picks the change up within a few seconds.
// online (optional) is whether the machine looks reachable right now; it only
// changes what the page says while a tool action waits.
export async function renderMachineSettingsInline(container, laptopID, label, online) {
  if (!container) return;
  container.innerHTML = `<div style="padding: 16px 20px; color: var(--text-tertiary); font-size: 13px;">Loading settings…</div>`;
  let loaded;
  try {
    loaded = await loadMachineSettings(laptopID);
  } catch (err) {
    container.innerHTML = `<div style="padding: 16px 20px; color: var(--danger); font-size: 13px;">Couldn't load settings: ${esc((err && err.message) || "unknown")}</div>`;
    return;
  }
  const who = esc(label || "this machine");
  container.dataset.msLaptop = laptopID;
  container.innerHTML = `
    <div class="ms-tools">${toolsHTML(loaded.effective, label)}</div>
    ${formHTML(loaded.values, loaded.lockedKeys, loaded.effective, "Changes save as you make them and reach " + (label || "this machine") + " within about 10 seconds. A 🔒 setting is set by your team.")}
    <div class="ms-status" role="status" aria-live="polite" style="padding: 10px 20px 16px; font-size: 12.5px; color: var(--text-tertiary);">Changes save automatically for ${who}.</div>`;
  wireSectionTabs(container);
  const refreshTools = async () => {
    try {
      const fresh = await loadMachineSettings(laptopID);
      const statuses = {};
      container.querySelectorAll("[data-tool-status]").forEach((el) => { statuses[el.dataset.toolStatus] = [el.textContent, el.style.color]; });
      const host = container.querySelector(".ms-tools");
      if (!host) return;
      host.innerHTML = toolsHTML(fresh.effective, label);
      for (const [tid, [text, color]] of Object.entries(statuses)) {
        const el = host.querySelector(`[data-tool-status="${CSS.escape(tid)}"]`);
        if (el) { el.textContent = text; el.style.color = color; }
      }
    } catch { /* the result line already says what happened */ }
  };
  // The picker re-renders into the same container for another machine, so
  // replace the previous render's handlers rather than stacking them: a
  // stacked handler would act on the machine shown before.
  if (container._msHandlers) {
    container.removeEventListener("click", container._msHandlers.click);
    container.removeEventListener("change", container._msHandlers.change);
  }
  const onClick = (e) => {
    const btn = e.target.closest("[data-tool-action]");
    if (!btn || btn.disabled) return;
    runToolAction(container, laptopID, label, online, btn, refreshTools);
  };
  container.addEventListener("click", onClick);
  const status = container.querySelector(".ms-status");
  const say = (text, color) => { status.textContent = text; status.style.color = color || "var(--text-tertiary)"; };
  let chain = Promise.resolve();
  const onChange = (e) => {
    if (!e.target.closest("[data-key]")) return;
    chain = chain.then(async () => {
      say("Saving…");
      try {
        const keys = await diffAndSave(laptopID, loaded.values, loaded.effective);
        if (!keys.length) { say("Changes save automatically for " + (label || "this machine") + "."); return; }
        const names = keys.map((k) => (MACHINE_SETTINGS_SPEC.find((s) => s.key === k) || { label: k }).label).join(", ");
        say("Saved " + names + ". " + (label || "This machine") + " applies it within about 10 seconds.", "var(--success)");
      } catch (err) {
        say("Not saved: " + ((err && err.message) || "unknown error"), "var(--danger)");
      }
    });
  };
  container.addEventListener("change", onChange);
  container._msHandlers = { click: onClick, change: onChange };
}

// renderMachineSettingsPreview shows every setting at its default, read-only,
// for when no machine on this login can receive changes: the page always
// shows what the settings are, never a blank.
export function renderMachineSettingsPreview(container) {
  if (!container) return;
  container.innerHTML = formHTML({}, [], {}, "Defaults shown. Link a computer to change them from here.");
  container.classList.add("ms-preview");
  container.querySelectorAll("input, select").forEach((el) => { el.disabled = true; });
  wireSectionTabs(container);
}
